<script setup lang="ts">
import type { MemberTag } from '~~/types/MemberTag'
import type { IonModal } from '@ionic/core/components/ion-modal'

const modelValue = defineModel<MemberTag[]>({
	default: () => [],
})

const sortedTags = computed(() => [...modelValue.value].sort((a, b) => (a.type === 'FUNCTIONAL') === (b.type === 'FUNCTIONAL')
	? a.name.localeCompare(b.name)
	: a.type === 'FUNCTIONAL' ? -1 : 1),
)

const props = defineProps<{
	label: string
	errorText?: string
	forbiddenTags?: string[]
}>()

const forbiddenTags = computed(() => (props.forbiddenTags ?? []).map(t => t.toLowerCase()))

const isForbiddenTag = (tag: string) => forbiddenTags.value.includes(tag.toLowerCase())

const isFunctionalTag = (tag: MemberTag) => tag.type === 'FUNCTIONAL'

const removeTag = (tagName: string) => {
	modelValue.value = modelValue.value.filter((tag: MemberTag) => tag.name !== tagName)
}

const addRoleModal = useTemplateRef<IonModal>('addRoleModal')
const newTag = ref('')
const newTagError = ref<string>()
const newTagInput = useTemplateRef('newTagInput')

const focusInput = async () => {
	await nextTick()
	await newTagInput.value?.$el?.setFocus()
}

const cancelAddRole = () => {
	modalController.dismiss()
	newTag.value = ''
}

const addTag = () => {
	const tagName = newTag.value.trim()
	if (!tagName) return

	modelValue.value = [...modelValue.value, { name: tagName } as MemberTag]
	modalController.dismiss()
	newTag.value = ''
}

watchDebounced(newTag, (val) => {
	const tagName = val.trim()
	if (!tagName) {
		newTagError.value = undefined
		return
	}
	if (isForbiddenTag(tagName)) {
		newTagError.value = `"${tagName}" ist nicht erlaubt.`
	} else if (modelValue.value.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
		newTagError.value = `"${tagName}" ist bereits hinzugefügt.`
	} else {
		newTagError.value = undefined
	}
})
</script>

<template>
	<ion-list>
		<ion-item
			v-if="modelValue.length === 0"
			lines="none"
			class="tag-item"
		>
			<ion-label class="tag-input-label">
				{{ label }}
			</ion-label>
			<ion-label class="tag-value">
				-
			</ion-label>
		</ion-item>
		<ion-item
			v-for="(tag, idx) in sortedTags"
			v-else
			:key="tag.name"
			:lines="idx === 0 || idx === sortedTags.length - 1 ? 'full' : 'none'"
			class="tag-item"
		>
			<ion-label class="tag-input-label">
				{{ idx === 0 ? label : '' }}
			</ion-label>
			<ion-item
				class="tag-value"
				:lines="idx === 0 || idx === sortedTags.length - 1 ? 'none' : 'full'"
			>
				<ion-label>
					{{ tag.name }}
				</ion-label>
				<i
					v-if="!isFunctionalTag(tag)"
					slot="end"
					class="delete-icon mdi mdi-close-circle"
					aria-label="Rolle entfernen"
					@click="removeTag(tag.name)"
				/>
			</ion-item>
		</ion-item>
		<ion-item
			lines="full"
			class="add-button-item"
		>
			<div>
				<VCIButton
					id="add-role"
					shape="round"
					icon="mdi-plus"
					aria-label="Rolle hinzufügen"
				/>
				<ion-label>Rolle hinzufügen</ion-label>
			</div>
			<ion-modal
				ref="addRoleModal"
				trigger="add-role"
				css-class="add-role-modal"
				:backdrop-dismiss="false"
				@ion-modal-did-present="focusInput"
			>
				<ion-content class="ion-padding">
					<ion-input
						ref="newTagInput"
						v-model="newTag"
						:label="label"
						fill="solid"
						label-placement="floating"
						:class="{ 'ion-invalid': !!newTagError, 'ion-touched': !!newTagError }"
						:error-text="newTagError"
					/>

					<div class="button-container">
						<ion-button
							expand="block"
							fill="clear"
							color="secondary"
							@click="cancelAddRole"
						>
							Abbrechen
						</ion-button>
						<ion-button
							expand="block"
							fill="clear"
							color="secondary"
							:disabled="!!newTagError || newTag.trim() === ''"
							@click="addTag"
						>
							Hinzufügen
						</ion-button>
					</div>
				</ion-content>
			</ion-modal>
		</ion-item>
	</ion-list>
</template>

<style lang="scss">
.add-role-modal {
	--width: 90%;
	--max-width: 320px;
	--height: 160px;
	--max-height: 160px;
	--min-height: 160px;
	--border-radius: 14px;
	--box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;

	ion-content::part(scroll) {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding-bottom: 5px;
	}

	ion-input {
		--background: #fff !important;
		--highlight-color-focused: var(--ion-color-secondary)
	}

	.button-container {
		margin-top: 20px;
		display: flex;
		flex-direction: row;
		gap: 10px;
		justify-content: space-between;
	}
}
</style>

<style lang="scss" scoped>
ion-list {
	padding-top: 0;
	margin-bottom: 10px;
}
.tag-item {
	display: flex;
	--inner-padding-end: 0;

	.tag-input-label {
		min-width: 20%;
		white-space: normal;
		font-size: 10px;
		flex: 1;
		margin-inline-end: 16px;
	}

	.tag-value {
		flex: 4;
		&::part(native) {
			padding: 0;
		}
	}

	.delete-icon {
		font-size: 20px;
		cursor: pointer;
	}
}

.add-button-item {
	& > div{
		display: flex;
		flex: 1;
		flex-direction: column;
		align-items: center;
		padding-top: 20px;
		#add-role {
			width: 52px;
			height: 52px;
		}
		ion-label {
			font-size: 14px;
			font-weight: 700;
			margin-bottom: 20px;
		}
	}
}
</style>
